import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Form, Input, Modal, Select, Spin, notification } from 'antd';
import { debounce } from 'lodash';
import CustomButton from 'components/custom-button';
import type { ManagerCandidate, StudioScopeMode, StudioSummary } from '../contract';
import { changeStudioManager, getManagerCandidates, useStudioViewer } from '../api';
import { mapStudioError } from '../studio-error';
import { MANAGER_CHANGE_WARNING, NO_MANAGER_TEXT, REASON_MAX, REASON_MIN } from '../labels';
import MemberCell from '../components/member-cell';
import styles from '../styles.module.scss';

// ------------------------------------------------------------ ô chọn Quản lý ---

export interface ManagerSelectProps {
  /** id tài khoản Quản lý đã chọn. */
  value?: number;
  onChange?: (id: number | undefined, candidate?: ManagerCandidate) => void;
  /** Quản lý hiện tại của Studio (đổi Quản lý): hiện mờ, không chọn lại được. */
  excludeId?: number | null;
  placeholder?: string;
  disabled?: boolean;
}

type ManagerOption = { value: number; label: string; disabled: boolean; candidate: ManagerCandidate };

/** Vì sao không chọn được (null = chọn được). */
function managerNote(c: ManagerCandidate, excludeId?: number | null): string | null {
  if (excludeId && c.id === excludeId) return 'Đang là Quản lý của Studio này.';
  if (!c.selectable) {
    return `Đang quản lý ${c.managingStudio?.title || (c.managingStudio ? `Studio #${c.managingStudio.id}` : 'Studio khác')}`;
  }
  return null;
}

/**
 * Chọn Quản lý Studio từ S3 `GET /studios/manager-candidates?q=` (MOD mọi phòng hoặc Agency, chưa khoá, ≤ 20 dòng;
 * mục 8.7.1). Người đang quản lý Studio khác hiện mờ kèm "Đang quản lý <tên Studio>". Dùng ở modal Tạo Studio
 * (bước Quản lý) và modal Đổi Quản lý.
 */
export const ManagerSelect = ({
  value,
  onChange,
  excludeId,
  placeholder = 'Tìm theo tên hoặc TikTok ID',
  disabled,
}: ManagerSelectProps) => {
  const [found, setFound] = useState<ManagerCandidate[]>([]);
  // Nhớ mọi ứng viên đã thấy để ô đã chọn vẫn hiện tên khi kết quả tìm kiếm đổi.
  const [known, setKnown] = useState<Record<number, ManagerCandidate>>({});
  const [fetching, setFetching] = useState(false);
  const [hint, setHint] = useState<string | null>(null);
  const seqRef = useRef(0);

  const load = useCallback(async (q?: string) => {
    const seq = ++seqRef.current;
    setFetching(true);
    try {
      const list = await getManagerCandidates(q);
      if (seq !== seqRef.current) return;
      const rows = Array.isArray(list) ? list : [];
      setFound(rows);
      setHint(rows.length ? null : 'Không có tài khoản MOD hoặc Agency phù hợp.');
      setKnown((prev) => {
        const next = { ...prev };
        rows.forEach((c) => {
          next[c.id] = c;
        });
        return next;
      });
    } catch (err) {
      if (seq !== seqRef.current) return;
      setFound([]);
      setHint(mapStudioError(err));
    } finally {
      if (seq === seqRef.current) setFetching(false);
    }
  }, []);

  const search = useMemo(() => debounce((q: string) => load(q.trim() || undefined), 400), [load]);

  useEffect(() => {
    load();
    return () => search.cancel();
  }, [load, search]);

  const options: ManagerOption[] = useMemo(() => {
    const list = [...found];
    if (value && !list.some((c) => c.id === value) && known[value]) list.unshift(known[value]);
    return list.map((c) => ({
      value: c.id,
      label: c.nickName || (c.username ? `@${c.username}` : `#${c.id}`),
      disabled: c.id !== value && !!managerNote(c, excludeId),
      candidate: c,
    }));
  }, [found, known, value, excludeId]);

  return (
    <Select<number, ManagerOption>
      showSearch
      allowClear
      filterOption={false}
      disabled={disabled}
      placeholder={placeholder}
      value={value}
      onSearch={search}
      onChange={(next) => onChange?.(next ?? undefined, next ? known[next] : undefined)}
      options={options}
      style={{ width: '100%' }}
      notFoundContent={
        fetching ? <Spin size="small" /> : hint ? <span className={styles.pickerReason}>{hint}</span> : null
      }
      optionRender={(option) => {
        const c = (option.data as ManagerOption).candidate;
        const note = managerNote(c, excludeId);
        return (
          <div className={styles.pickerOption}>
            <MemberCell
              nickName={c.nickName}
              username={c.username}
              avatar={c.avatar}
              size={28}
              sub={c.username ? `@${c.username}` : null}
            />
            {note && <span className={styles.pickerReason}>{note}</span>}
          </div>
        );
      }}
    />
  );
};

// ------------------------------------------------------------ modal ---

export interface ChangeManagerModalProps {
  open: boolean;
  /** Studio cần đổi Quản lý (StudioSummary / StudioListItem đều được). */
  studio: Pick<StudioSummary, 'id' | 'title' | 'manager'> | null;
  /** STUDIO_SCOPE_MODE; không truyền thì lấy từ GET /studios/mine (đã nhớ, không gọi thêm). */
  scopeMode?: StudioScopeMode | null;
  onClose: () => void;
  onDone?: (studio: StudioSummary) => void;
}

interface FormValues {
  managerId?: number;
  reason: string;
}

const reasonRules = [
  {
    validator: (_: unknown, value?: string) =>
      (value || '').trim().length >= REASON_MIN
        ? Promise.resolve()
        : Promise.reject(new Error(`Lý do cần ít nhất ${REASON_MIN} ký tự.`)),
  },
];

/**
 * Admin đổi Quản lý của một Studio (S7, mục 8.7.1): Quản lý mới (cùng ô chọn với bước Quản lý của modal Tạo
 * Studio) + lý do bắt buộc ≥ 5 ký tự. Câu cảnh báo theo `scopeMode` (MANAGER_CHANGE_WARNING, không khuyên đổi
 * phòng ban). Lỗi hiện đúng câu máy chủ trả trong modal.
 */
const ChangeManagerModal = ({ open, studio, scopeMode, onClose, onDone }: ChangeManagerModalProps) => {
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const viewer = useStudioViewer();
  const mode = scopeMode ?? viewer.scopeMode;

  const handleClose = () => {
    if (saving) return;
    form.resetFields();
    setError(null);
    onClose();
  };

  const onFinish = async (values: FormValues) => {
    if (!studio || !values.managerId) return;
    setSaving(true);
    setError(null);
    try {
      const row = await changeStudioManager(studio.id, {
        managerId: values.managerId,
        reason: values.reason.trim(),
      });
      notification.success({
        message: `Đã đổi Quản lý của ${studio.title}${row?.manager ? ` sang ${row.manager.nickName}` : ''}.`,
      });
      form.resetFields();
      onDone?.(row);
      onClose();
    } catch (err) {
      setError(mapStudioError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={studio ? `Đổi Quản lý · ${studio.title}` : 'Đổi Quản lý'}
      open={open && !!studio}
      onCancel={handleClose}
      footer={null}
      destroyOnHidden
      maskClosable={false}
      width={600}
    >
      <p style={{ marginTop: 0 }}>
        Quản lý hiện tại: <strong>{studio?.manager?.nickName || NO_MANAGER_TEXT}</strong>
      </p>
      {mode && (
        <Alert type="warning" showIcon message={MANAGER_CHANGE_WARNING[mode]} style={{ marginBottom: 16 }} />
      )}
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
      <Form form={form} layout="vertical" className="custom-form" onFinish={onFinish}>
        <Form.Item
          label="Quản lý mới"
          name="managerId"
          extra="Chọn được tài khoản MOD hoặc Agency chưa quản lý Studio nào."
          rules={[{ required: true, message: 'Chọn Quản lý mới.' }]}
        >
          <ManagerSelect excludeId={studio?.manager?.id ?? null} />
        </Form.Item>
        <Form.Item label="Lý do" name="reason" rules={reasonRules}>
          <Input.TextArea rows={3} maxLength={REASON_MAX} showCount placeholder="Nhập lý do" />
        </Form.Item>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <CustomButton text="Huỷ" bgColor="primary-grey" onClick={handleClose} />
          <CustomButton
            text="Đổi Quản lý"
            bgColor="secondary-green"
            htmlType="submit"
            loading={saving}
          />
        </div>
      </Form>
    </Modal>
  );
};

export default ChangeManagerModal;
